import React from 'react';
import List from '@mui/material/List';
import { Box, ListItemIcon, IconButton } from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

//title icon
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessIcon from '@mui/icons-material/Business';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BuildIcon from '@mui/icons-material/Build';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import InventoryIcon from '@mui/icons-material/Inventory';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FlagIcon from '@mui/icons-material/Flag';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ChatIcon from '@mui/icons-material/Chat';
import ApprovalIcon from '@mui/icons-material/Approval';

import { NavLink as RouterLink } from 'react-router-dom';

/**
 * 이 메뉴가 지금 화면인지.
 *
 * NavLink 가 주는 isActive 는 앞부분만 같아도 참이라, 상위 경로 메뉴까지 같이 켜진다.
 * (/targetmng/kpi-setting 에서 /targetmng 메뉴가 함께 칠해지던 문제)
 * MenuList 가 골라준 "가장 구체적인 한 개" 와 같은지만 본다.
 */
const isCurrent = (link, activeLink) => {
  if (!link || !activeLink) return false;
  const to = link.startsWith('/') ? link : `/${link}`;
  return to === activeLink;
};

const getTitleIcon = (icon) => {
  switch (icon) {
    case 'user':
      return <AccountCircleIcon />;
    case 'project':
      return <AccountTreeIcon />;
    case 'goal':
      return <FlagIcon />;
    case 'order':
      return <AssignmentIcon />;
    case 'sales':
      return <AttachMoneyIcon />;
    case 'bond':
      return <CurrencyExchangeIcon />;
    case 'tech':
      return <BuildIcon />;
    case 'eis':
      return <LibraryBooksIcon />;
    case 'statistics':
      return <StackedLineChartIcon />;
    case 'product':
      return <InventoryIcon />;
    case 'partner':
      return <BusinessIcon />;
    case 'common':
      return <SettingsIcon />;
    case 'cost':
      return <PriceCheckIcon />;
    case 'budget':
      return <CreditScoreIcon />;
    case 'dashboard':
      return <DashboardIcon />;
    case 'chat':
      return <ChatIcon />;
    case 'approval':
      return <ApprovalIcon />;
    default:
      return '';
  }
};

// 메뉴가 50개 가까이 되어 한 줄 48px 로는 너무 길었다(2026-10-11). PC 에서는 글씨는 그대로 두고
// 위아래 여백만 줄인다. 모바일은 손가락으로 눌러야 하므로 기본 높이를 유지한다.
const DENSE_ROW = { minHeight: { xs: 48, md: 34 }, py: { xs: 1, md: 0.25 } };
const DENSE_TEXT = { my: { xs: 0.5, md: 0 } };
const DENSE_ICON = { minWidth: { xs: 56, md: 40 } };
// 대메뉴(그룹 제목)를 하위 메뉴와 한눈에 구분되게: 옅은 배경 + 진한 굵은 글씨 + 아이콘 색.
// 선택된 화면 강조색(#37474f)과 같은 blue-grey 계열로 맞춘다.
const GROUP_HEADER = {
  bgcolor: '#eceff1',
  '&:hover': { bgcolor: '#e0e5e8' },
  '& .MuiListItemIcon-root': { color: '#37474f' },
};
const GROUP_TITLE = { fontWeight: 700, color: '#263238' };
const SUB_TITLE = { color: '#455a64' };
// 즐겨찾기 그룹은 다른 그룹과 한눈에 구분되도록 호박색(amber) 톤으로 강조한다.
const FAV_HEADER = {
  bgcolor: '#fff8e1',
  '&:hover': { bgcolor: '#ffecb3' },
  '& .MuiListItemIcon-root': { color: '#f9a825' },
};
const FAV_TITLE = { fontWeight: 700, color: '#8d6e00' };

const MenuItem = ({
  menu,
  handleDrawerClose,
  onMenuSelect,
  activeLink,
  open,
  onToggleOpen,
  variant, // 'favorites' 면 즐겨찾기 그룹 전용 스타일/빈 상태 힌트를 쓴다
  isFavorite,
  onToggleFavorite,
}) => {
  const isFavoritesGroup = variant === 'favorites';
  // 즐겨찾기 그룹은 항목이 0개여도 "그룹+Collapse" 형태를 유지한다(빈 상태 힌트를 보여줘야 해서).
  const hasItems = (menu.items && menu.items.length > 0) || isFavoritesGroup;
  const handleMenuSelect = () => {
    if (onMenuSelect) {
      window.setTimeout(onMenuSelect, 0);
      return;
    }

    if (handleDrawerClose) {
      handleDrawerClose();
    }
  };

  const handleClick = () => {
    if (onToggleOpen) onToggleOpen();
  };

  const renderFavoriteStar = (item) => {
    if (!onToggleFavorite) return null;
    const favored = isFavorite ? isFavorite(item) : false;
    return (
      <IconButton
        className="fav-star-btn"
        size="small"
        aria-label={favored ? '즐겨찾기 해제' : '즐겨찾기 추가'}
        onClick={(e) => {
          // 별 클릭은 즐겨찾기 토글만 하고 메뉴 이동(Link 네비게이션)은 막는다.
          e.preventDefault();
          e.stopPropagation();
          onToggleFavorite(item);
        }}
        sx={{
          position: 'absolute',
          right: 4,
          top: '50%',
          transform: 'translateY(-50%)',
          // 데스크탑은 마우스를 올렸을 때만 보이고, 이미 즐겨찾기된 건 항상 보인다.
          // 모바일(xs)은 호버가 없으므로 항상 보인다.
          opacity: { xs: 1, md: favored ? 1 : 0 },
          color: favored ? '#f9a825' : '#9e9e9e',
          p: 0.5,
          zIndex: 1,
        }}
      >
        {favored ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
      </IconButton>
    );
  };

  // 서브 아이템 없는 경우: 직접 링크
  if (!hasItems) {
    return (
      <Box>
        <ListItemButton
          component={RouterLink}
          to={menu.link}
          onClick={handleMenuSelect}
          sx={{ borderRadius: '4px', mx: 1, my: { xs: 1, md: 0.25 }, px: 1, ...DENSE_ROW }}
          style={() => {
            const active = isCurrent(menu.link, activeLink);
            return {
              background: active ? '#37474f' : '',
              color: active ? '#fff' : '',
            };
          }}
        >
          {menu.icon && <ListItemIcon sx={DENSE_ICON}>{getTitleIcon(menu.icon)}</ListItemIcon>}
          <ListItemText
            sx={DENSE_TEXT}
            primary={
              <Typography variant="subtitle2" component="div" sx={{ whiteSpace: 'pre-line' }}>
                {menu.title}
              </Typography>
            }
          />
        </ListItemButton>
      </Box>
    );
  }

  return (
    <Box>
      <ListItemButton
        onClick={handleClick}
        sx={{
          borderRadius: '4px',
          mx: 1,
          my: { xs: 1, md: 0.25 },
          mt: { xs: 1, md: 0.75 },
          px: 1,
          ...DENSE_ROW,
          ...(isFavoritesGroup ? FAV_HEADER : GROUP_HEADER),
        }}
      >
        {(isFavoritesGroup || menu.icon) && (
          <ListItemIcon sx={DENSE_ICON}>
            {isFavoritesGroup ? <StarIcon sx={{ color: '#f9a825' }} /> : getTitleIcon(menu.icon)}
          </ListItemIcon>
        )}
        <ListItemText
          sx={DENSE_TEXT}
          primary={
            <Typography
              variant="subtitle2"
              component="div"
              sx={{ whiteSpace: 'pre-line', ...(isFavoritesGroup ? FAV_TITLE : GROUP_TITLE) }}
            >
              {menu.title}
            </Typography>
          }
        />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {isFavoritesGroup && (!menu.items || menu.items.length === 0) ? (
            // 즐겨찾기가 하나도 없을 때: 어떻게 추가하는지 한 줄로 안내한다.
            <Typography
              variant="caption"
              component="div"
              sx={{
                color: '#9e9e9e',
                pl: { xs: '56px', md: '48px' },
                pr: 2,
                py: { xs: 1, md: 0.75 },
              }}
            >
              메뉴 옆 ☆를 눌러 추가
            </Typography>
          ) : (
            menu.items.map((item, index) => (
              <Box
                key={item.uuid || item.id || index}
                sx={{
                  position: 'relative',
                  '&:hover .fav-star-btn': { opacity: 1 },
                }}
              >
                <ListItemButton
                  component={RouterLink}
                  to={item.link}
                  onClick={handleMenuSelect}
                  sx={{
                    borderRadius: '4px',
                    mx: 1,
                    px: 1,
                    pr: { xs: 5, md: 4.5 },
                    paddingLeft: { xs: '56px', md: '48px' },
                    ...DENSE_ROW,
                    // 글자색은 버튼에 둔다 - 선택된 메뉴는 style 의 흰 글씨가 이걸 덮어써야 해서
                    ...SUB_TITLE,
                  }}
                  style={() => {
                    const active = isCurrent(item.link, activeLink);
                    return {
                      background: active ? '#37474f' : '',
                      color: active ? '#fff' : '',
                    };
                  }}
                >
                  <ListItemText
                    sx={DENSE_TEXT}
                    primary={
                      <Typography
                        sx={{ whiteSpace: 'pre-line' }}
                        variant="body2"
                        component="div"
                      >
                        {item.title}
                      </Typography>
                    }
                  />
                </ListItemButton>
                {renderFavoriteStar(item)}
              </Box>
            ))
          )}
        </List>
      </Collapse>
    </Box>
  );
};

export default MenuItem;
